import type { z } from 'zod';
import {
    createPillarApiRequestExecutor,
    dedupe,
    type HttpRequestHeaders,
    type PillarApiClientOptions,
    type PillarApiRequestOptions,
} from './pillar-api-core';
import type {
    ActiveRoleAssignmentResponse,
    SessionUserProfileDetailedResponse,
    StartImpersonationRequest,
    SwapCookieForTokenResponse,
    NativeAppLoginLinkResponse,
    SwapTokenForCookieRequest,
    SwapTokenForCookieResponse,
} from './auth.types';
import type {
    CredentialRotationBeginRequest,
    CredentialRotationBeginResponse,
    CredentialRotationFailRequest,
    CredentialRotationInjectedRequest,
    CredentialRotationPromoteRequest,
    CredentialRotationPromoteResponse,
    CredentialRotationSubmittedRequest,
    IpxCredentialRuntimeBootstrapRequest,
    IpxCredentialRuntimeBootstrapResponse,
    IpxCredentialRuntimeResolveRequest,
    IpxCredentialRuntimeResolveResponse,
    ManualCredentialCaptureRequest,
    ManualCredentialCaptureResponse,
} from './secret.types';
import type {
    IpxRbsWorkflowRuntimeBootstrapRequest,
    IpxRbsWorkflowRuntimeBootstrapResponse,
} from './rbs-ips-workflow.types';
import type { MerchantConfig } from './backend_config.types';
import type {
    CloverOAuthAuthorizationStatus,
    RenewCloverOAuthAuthorizationResponse,
} from './types/ipg/clover/clover-heartbeat.types';
import type { EffectiveConfigResponse } from './samba-config.types';
import type { NavigateMenuWebsitesResponse } from './samba-config.types';
import type { LogIngestRequest } from './logging.types';
import type {
    AllowedWebsiteTemplateMutationResponse,
    OrganizationAllowedWebsiteMutationResponse,
} from './allowed-website-template.types';
import type {
    ProcessAchPaymentRequest,
    ProcessAchPaymentResponse,
    ProcessCardTokenPaymentResponse,
    ProcessCardNotPresentPaymentResponse,
    ProcessCloverCardNotPresentPaymentRequest,
    RecordTokenRequest,
    RecordTokenResponse,
    SambaGatewayTransactionDetailsFetchResult,
    TerminalAbortRequest,
    TerminalAbortResponse,
    TerminalSaleRequest,
    TerminalSaleResponse,
} from './samba-payment.types';
import type {
    BinLookupRequest,
    BinLookupResponse,
    ProcessStoredPaymentMethodPaymentRequest,
    SubmitCardTokenPaymentRequest,
} from './payment.types';
import type {
    MessageResponse,
    SambaInstallerQaQuery,
    SambaLatestReleaseQuery,
    SambaLatestReleaseResponse,
    SambaVersionResponse,
} from './common.types';
import type { WindowsArtifactQa } from './windows-artifact-qa.types';
import type { SecretWithCredentialsResponse } from './secret.types';
import {
    ActiveRoleAssignmentResponseSchema,
    SessionUserProfileDetailedResponseSchema,
    StartImpersonationRequestSchema,
    SwapCookieForTokenResponseSchema,
    NativeAppLoginLinkResponseSchema,
    SwapTokenForCookieRequestSchema,
    SwapTokenForCookieResponseSchema,
} from './schemas/auth.schemas';
import {
    CredentialRotationBeginRequestSchema,
    CredentialRotationBeginResponseSchema,
    CredentialRotationFailRequestSchema,
    CredentialRotationInjectedRequestSchema,
    CredentialRotationPromoteRequestSchema,
    CredentialRotationPromoteResponseSchema,
    CredentialRotationSubmittedRequestSchema,
    IpxCredentialRuntimeBootstrapRequestSchema,
    IpxCredentialRuntimeBootstrapResponseSchema,
    IpxCredentialRuntimeResolveRequestSchema,
    IpxCredentialRuntimeResolveResponseSchema,
    ManualCredentialCaptureRequestSchema,
    ManualCredentialCaptureResponseSchema,
} from './schemas/secret.schemas';
import {
    IpxRbsWorkflowRuntimeBootstrapRequestSchema,
    IpxRbsWorkflowRuntimeBootstrapResponseSchema,
} from './schemas/rbs-ips-workflow.schemas';
import {
    ConfigResponseSchema,
    EffectiveConfigResponseSchema,
    NavigateMenuWebsitesResponseSchema,
} from './schemas/samba-config.schemas';
import { LogIngestRequestSchema } from './schemas/logging.schemas';
import {
    ProcessAchPaymentResponseSchema,
    ProcessCardTokenPaymentResponseSchema,
    RecordTokenResponseSchema,
    SambaGatewayTransactionDetailsFetchResultSchema,
    TerminalAbortResponseSchema,
    TerminalSaleResponseSchema,
} from './schemas/samba-payment.schemas';
import {
    BinLookupResponseSchema,
    ProcessCardNotPresentPaymentResponseSchema,
    ProcessStoredPaymentMethodPaymentRequestSchema,
} from './schemas/payment.schemas';
import { ProcessCloverCardNotPresentPaymentRequestSchema } from './schemas/ipg/clover/clover-card-not-present-payment.schemas';
import {
    CloverOAuthAuthorizationStatusSchema,
    RenewCloverOAuthAuthorizationRequestSchema,
    RenewCloverOAuthAuthorizationResponseSchema,
} from './schemas/ipg/clover/clover-heartbeat.schemas';
import {
    SambaLatestReleaseResponseSchema,
    SambaInstallerQaQuerySchema,
    SambaVersionResponseSchema,
    MessageResponseSchema,
    VoidResponseSchema,
} from './schemas/common.schemas';
import { WindowsArtifactQaSchema } from './schemas/windows-artifact-qa.schemas';
import { SecretWithCredentialsResponseSchema } from './schemas/secret.schemas';
import {
    AllowedWebsiteTemplateMutationResponseSchema,
    OrganizationAllowedWebsiteMutationResponseSchema,
    AllowedWebsiteTemplateUuidRequestSchema,
} from './schemas/allowed-website-template.schemas';

export function createPillarApiWithOptions(options: PillarApiClientOptions) {
    const { baseUrl, requestWithSchema } = createPillarApiRequestExecutor(options);

    const authApi = {
        getProfile: () =>
            requestWithSchema<SessionUserProfileDetailedResponse>(
                '/portal/v1/user_management/any_user/profile',
                SessionUserProfileDetailedResponseSchema
            ),
        getActiveRoleAssignment: () =>
            requestWithSchema<ActiveRoleAssignmentResponse | null>(
                '/portal/v1/user_management/any_user/active_role_assignment',
                ActiveRoleAssignmentResponseSchema.nullable()
            ),
        swapCookieForToken: () =>
            requestWithSchema<SwapCookieForTokenResponse>(
                '/portal/v1/user_management/any_user/swap_cookie_for_token',
                SwapCookieForTokenResponseSchema,
                {
                    method: 'POST',
                }
            ),
        issueNativeAppLoginLink: () =>
            requestWithSchema<NativeAppLoginLinkResponse>(
                '/portal/v1/user_management/any_user/issue_native_app_login_link',
                NativeAppLoginLinkResponseSchema,
                {
                    method: 'POST',
                }
            ),
        swapTokenForCookie: (data: SwapTokenForCookieRequest) =>
            requestWithSchema<SwapTokenForCookieResponse>(
                '/portal/v1/user_management/any_user/swap_token_for_cookie',
                SwapTokenForCookieResponseSchema,
                {
                    method: 'POST',
                    body: SwapTokenForCookieRequestSchema.parse(data),
                    handle401AsSessionExpired: false,
                }
            ),
        startImpersonation: (role_assignment_uuid: string) =>
            requestWithSchema<MessageResponse>(
                '/portal/v1/user_management/any_user/start_impersonation',
                MessageResponseSchema,
                {
                    method: 'POST',
                    body: StartImpersonationRequestSchema.parse({
                        role_assignment_uuid,
                    }) satisfies StartImpersonationRequest,
                }
            ),
        stopImpersonation: () =>
            requestWithSchema<MessageResponse>(
                '/portal/v1/user_management/any_user/stop_impersonation',
                MessageResponseSchema,
                {
                    method: 'POST',
                }
            ),
    };

    const paymentsInfrastructureApi = {
        getCloverOAuthAuthorizationStatus: (merchantOrganizationUuid: string) =>
            requestWithSchema<CloverOAuthAuthorizationStatus>(
                `/clover/oauth/status?${new URLSearchParams({
                    organization_uuid: RenewCloverOAuthAuthorizationRequestSchema.parse({
                        organization_uuid: merchantOrganizationUuid,
                    }).organization_uuid,
                }).toString()}`,
                CloverOAuthAuthorizationStatusSchema
            ),
        renewCloverOAuthAuthorization: (merchantOrganizationUuid: string) => {
            const request = RenewCloverOAuthAuthorizationRequestSchema.parse({
                organization_uuid: merchantOrganizationUuid,
            });
            return requestWithSchema<RenewCloverOAuthAuthorizationResponse>(
                '/clover/oauth/renew',
                RenewCloverOAuthAuthorizationResponseSchema,
                { method: 'POST', body: request }
            );
        },
    };

    const sambaConfigApi = {
        getMyOrgConfig: () =>
            requestWithSchema<MerchantConfig>(
                '/portal/v1/samba_config/any_user/org',
                ConfigResponseSchema
            ),
        getMyEffectiveConfig: () =>
            requestWithSchema<EffectiveConfigResponse>(
                '/portal/v2/samba_config/any_user/effective',
                EffectiveConfigResponseSchema
            ),
        getMyNavigateMenuWebsites: () =>
            requestWithSchema<NavigateMenuWebsitesResponse>(
                '/portal/v1/samba_config/any_user/navigate_menu',
                NavigateMenuWebsitesResponseSchema
            ),
    };

    const paymentsApi = {
        binLookup: dedupe((data: BinLookupRequest) =>
            requestWithSchema<BinLookupResponse>(
                '/portal/v1/payments/bin-lookup',
                BinLookupResponseSchema,
                { method: 'POST', body: data }
            )
        ),

        terminalSale: dedupe((data: TerminalSaleRequest) =>
            requestWithSchema<TerminalSaleResponse>(
                '/portal/v1/payments/terminal/sale',
                TerminalSaleResponseSchema,
                {
                    method: 'POST',
                    body: data,
                }
            )
        ),

        terminalAbort: dedupe((data: TerminalAbortRequest) =>
            requestWithSchema<TerminalAbortResponse>(
                '/portal/v1/payments/terminal/abort',
                TerminalAbortResponseSchema,
                {
                    method: 'POST',
                    body: data,
                }
            )
        ),

        recordToken: dedupe((data: RecordTokenRequest) =>
            requestWithSchema<RecordTokenResponse>(
                '/portal/v1/payments/method/card-token',
                RecordTokenResponseSchema,
                {
                    method: 'POST',
                    body: data,
                }
            )
        ),

        processCardTokenPayment: dedupe((data: SubmitCardTokenPaymentRequest) =>
            requestWithSchema<ProcessCardTokenPaymentResponse>(
                '/portal/v1/payments/payment/card-token',
                ProcessCardTokenPaymentResponseSchema,
                {
                    method: 'POST',
                    body: data,
                }
            )
        ),

        processCloverCardNotPresentPayment: dedupe(
            (data: ProcessCloverCardNotPresentPaymentRequest) =>
                requestWithSchema<ProcessCardNotPresentPaymentResponse>(
                    '/portal/v1/payments/payment/clover/card-not-present',
                    ProcessCardNotPresentPaymentResponseSchema,
                    {
                        method: 'POST',
                        body: ProcessCloverCardNotPresentPaymentRequestSchema.parse(data),
                    }
                )
        ),

        processStoredPaymentMethodPayment: dedupe(
            (data: ProcessStoredPaymentMethodPaymentRequest) =>
                requestWithSchema<ProcessCardTokenPaymentResponse>(
                    '/portal/v1/payments/payment/stored-method',
                    ProcessCardTokenPaymentResponseSchema,
                    {
                        method: 'POST',
                        body: ProcessStoredPaymentMethodPaymentRequestSchema.parse(data),
                    }
                )
        ),

        processAchPayment: dedupe((data: ProcessAchPaymentRequest) =>
            requestWithSchema<ProcessAchPaymentResponse>(
                '/portal/v1/payments/ach',
                ProcessAchPaymentResponseSchema,
                {
                    method: 'POST',
                    body: data,
                }
            )
        ),

        refreshGatewayTransactionDetails: dedupe((paymentTransactionUuid: string) =>
            requestWithSchema<SambaGatewayTransactionDetailsFetchResult>(
                `/portal/v1/payments/vault/transactions/${encodeURIComponent(paymentTransactionUuid)}/gateway-details/refresh`,
                SambaGatewayTransactionDetailsFetchResultSchema,
                {
                    method: 'POST',
                }
            )
        ),
    };

    const sambaApi = {
        getVersion: (headers?: HttpRequestHeaders) =>
            requestWithSchema<SambaVersionResponse>(
                '/samba/v1/version',
                SambaVersionResponseSchema,
                {
                    headers,
                }
            ),
        getLatestRelease: (params: SambaLatestReleaseQuery, headers?: HttpRequestHeaders) => {
            const queryParams: URLSearchParams = new URLSearchParams({
                platform: params.platform,
                arch: params.arch,
            });
            return requestWithSchema<SambaLatestReleaseResponse>(
                `/samba/latest?${queryParams.toString()}`,
                SambaLatestReleaseResponseSchema,
                {
                    headers,
                }
            );
        },
        getInstallerQa: (params: SambaInstallerQaQuery, headers?: HttpRequestHeaders) => {
            const query = SambaInstallerQaQuerySchema.parse(params);
            const queryParams = new URLSearchParams({
                track: query.track,
                arch: query.arch,
            });
            return requestWithSchema<WindowsArtifactQa>(
                `/samba/installer-qa?${queryParams.toString()}`,
                WindowsArtifactQaSchema,
                { headers }
            );
        },
    };

    const secretsApi = {
        getSecretForUse: (organizationUuid: string, systemId: number, accountId: string) =>
            requestWithSchema<SecretWithCredentialsResponse>(
                `/portal/v1/secrets/${organizationUuid}/${systemId}/${accountId}/use-credentials`,
                SecretWithCredentialsResponseSchema
            ),
    };

    const rpaCredentialInjectionApi = {
        bootstrap: dedupe((data: IpxCredentialRuntimeBootstrapRequest) =>
            requestWithSchema<IpxCredentialRuntimeBootstrapResponse>(
                '/portal/v1/rpa-credential-injection/bootstrap',
                IpxCredentialRuntimeBootstrapResponseSchema,
                {
                    method: 'POST',
                    body: IpxCredentialRuntimeBootstrapRequestSchema.parse(data),
                }
            )
        ),
        resolve: dedupe((data: IpxCredentialRuntimeResolveRequest) =>
            requestWithSchema<IpxCredentialRuntimeResolveResponse>(
                '/portal/v1/rpa-credential-injection/resolve',
                IpxCredentialRuntimeResolveResponseSchema,
                {
                    method: 'POST',
                    body: IpxCredentialRuntimeResolveRequestSchema.parse(data),
                }
            )
        ),
        rotationBegin: dedupe((data: CredentialRotationBeginRequest) =>
            requestWithSchema<CredentialRotationBeginResponse>(
                '/portal/v1/rpa-credential-injection/rotation/begin',
                CredentialRotationBeginResponseSchema,
                {
                    method: 'POST',
                    body: CredentialRotationBeginRequestSchema.parse(data),
                }
            )
        ),
        rotationInjected: dedupe((data: CredentialRotationInjectedRequest) =>
            requestWithSchema<void>(
                '/portal/v1/rpa-credential-injection/rotation/injected',
                VoidResponseSchema,
                {
                    method: 'POST',
                    body: CredentialRotationInjectedRequestSchema.parse(data),
                    acceptedStatusCodes: [204],
                }
            )
        ),
        rotationSubmitted: dedupe((data: CredentialRotationSubmittedRequest) =>
            requestWithSchema<void>(
                '/portal/v1/rpa-credential-injection/rotation/submitted',
                VoidResponseSchema,
                {
                    method: 'POST',
                    body: CredentialRotationSubmittedRequestSchema.parse(data),
                    acceptedStatusCodes: [204],
                }
            )
        ),
        rotationPromote: dedupe((data: CredentialRotationPromoteRequest) =>
            requestWithSchema<CredentialRotationPromoteResponse>(
                '/portal/v1/rpa-credential-injection/rotation/promote',
                CredentialRotationPromoteResponseSchema,
                {
                    method: 'POST',
                    body: CredentialRotationPromoteRequestSchema.parse(data),
                }
            )
        ),
        rotationFail: dedupe((data: CredentialRotationFailRequest) =>
            requestWithSchema<void>(
                '/portal/v1/rpa-credential-injection/rotation/fail',
                VoidResponseSchema,
                {
                    method: 'POST',
                    body: CredentialRotationFailRequestSchema.parse(data),
                    acceptedStatusCodes: [204],
                }
            )
        ),
        manualCapture: dedupe((data: ManualCredentialCaptureRequest) =>
            requestWithSchema<ManualCredentialCaptureResponse>(
                '/portal/v1/rpa-credential-injection/manual-capture',
                ManualCredentialCaptureResponseSchema,
                {
                    method: 'POST',
                    body: ManualCredentialCaptureRequestSchema.parse(data),
                }
            )
        ),
    };

    const rbsIpsWorkflowRuntimeApi = {
        bootstrap: dedupe((data: IpxRbsWorkflowRuntimeBootstrapRequest) =>
            requestWithSchema<IpxRbsWorkflowRuntimeBootstrapResponse>(
                '/portal/v1/rbs-ips-workflows/runtime/bootstrap',
                IpxRbsWorkflowRuntimeBootstrapResponseSchema,
                {
                    method: 'POST',
                    body: IpxRbsWorkflowRuntimeBootstrapRequestSchema.parse(data),
                }
            )
        ),
    };

    const loggingApi = {
        ingestLogs: (data: LogIngestRequest) => {
            const requestBody: LogIngestRequest = LogIngestRequestSchema.parse(data);
            return requestWithSchema<void>('/portal/v1/logs', VoidResponseSchema, {
                method: 'POST',
                body: requestBody,
            });
        },
    };

    const allowedWebsiteTemplatesApi = {
        getTemplate: (templateUuid: string) =>
            requestWithSchema<AllowedWebsiteTemplateMutationResponse>(
                `/portal/v1/allowed_website_templates/${templateUuid}`,
                AllowedWebsiteTemplateMutationResponseSchema
            ),
    };

    const rpaBuilderApi = {
        // Traceability: supports RBS-MERCHANT-SESSION-001.
        getOrganizationAllowedWebsite: (
            organizationUuid: string,
            organizationWebsiteUuid: string
        ) =>
            requestWithSchema<OrganizationAllowedWebsiteMutationResponse>(
                `/portal/v1/organization/${encodeURIComponent(organizationUuid)}/allowed_websites/${encodeURIComponent(organizationWebsiteUuid)}`,
                OrganizationAllowedWebsiteMutationResponseSchema
            ),
        getAllowedWebsiteTemplate: (templateUuid: string) =>
            requestWithSchema<AllowedWebsiteTemplateMutationResponse>(
                `/portal/v1/rpa-builder/allowed-website-templates/${encodeURIComponent(
                    AllowedWebsiteTemplateUuidRequestSchema.parse({
                        allowed_website_template_uuid: templateUuid,
                    }).allowed_website_template_uuid
                )}`,
                AllowedWebsiteTemplateMutationResponseSchema
            ),
    };

    return {
        baseUrl,
        requestWithSchema: <T>(
            endpoint: string,
            schema: z.ZodSchema<T>,
            requestOptions?: PillarApiRequestOptions
        ): Promise<T> => {
            return requestWithSchema(endpoint, schema, requestOptions);
        },
        authApi,
        paymentsInfrastructureApi,
        sambaConfigApi,
        paymentsApi,
        sambaApi,
        secretsApi,
        rpaCredentialInjectionApi,
        rbsIpsWorkflowRuntimeApi,
        loggingApi,
        allowedWebsiteTemplatesApi,
        rpaBuilderApi,
    };
}

export type SambaPillarApiClient = ReturnType<typeof createPillarApiWithOptions>;
